import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Autocomplete, Stack, TextField } from "@mui/material";
import { Typography } from "@platform/design-system";

import { type Carrier, useCarriers } from "../hooks/useCarriers";

import type { Theme } from "@mui/material/styles";

export interface CarrierSelectorProps {
  rfpId?: string;
  carrier: Carrier | null;
  onSelect: (carrier: Carrier | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const CarrierSelector = ({
  rfpId,
  carrier,
  onSelect,
  placeholder = "Select Carrier...",
  disabled = false,
}: CarrierSelectorProps): React.JSX.Element => {
  const { carriers, isLoading } = useCarriers({ rfpId });

  return (
    <Autocomplete<Carrier>
      options={carriers}
      noOptionsText={
        <Stack direction="column" spacing={0}>
          <Typography>
            <strong>Carrier not found</strong>
          </Typography>
          <Typography>
            ”Carrier” was not found in our database. Please contact
            support@centrobenefits.com to add this carrier. Adding a new carrier
            will take some time to process.
          </Typography>
        </Stack>
      }
      value={carrier}
      loading={isLoading}
      disabled={isLoading || disabled}
      fullWidth
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_event, selected) => onSelect(selected)}
      popupIcon={<ExpandMoreIcon />}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          size="small"
          helperText={carrier?.id === "other" ? "" : "min 3 characters"}
        />
      )}
      sx={{
        "& .MuiOutlinedInput-root": {
          minHeight: "36.5px",
          py: 0,
          fontSize: "0.875rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: (theme: Theme) => theme.palette.divider,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: (theme: Theme) => theme.palette.text.secondary,
        },
        "& .MuiInputBase-input": {
          color: (theme: Theme) => theme.palette.text.primary,
        },
        "& .MuiInputBase-input::placeholder": {
          color: (theme: Theme) => theme.palette.text.secondary,
          opacity: 1,
        },
        "& .MuiAutocomplete-popupIndicator": {
          transform: "none",
        },
      }}
    />
  );
};
